import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCurrentLang } from "@/hooks/useLanguage";
import { getMemberPortalUrl } from "@/constants/links";

interface NavChild {
  label: string;
  href: string;
}

interface NavItem {
  label: string;
  href: string;
  internal?: boolean;
  children?: NavChild[];
}

export default function Navbar() {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [mobileSearch, setMobileSearch] = useState("");
  const [activeSection, setActiveSection] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const lang = useCurrentLang();
  const isFrench = lang === "fr";
  const prefix = isFrench ? "/fr" : "";

  // Determine if we're on the home page (English or French)
  const isOnHomePage = location.pathname === "/" || location.pathname === "" || location.pathname === "/fr" || location.pathname === "/fr/";

  const navItems: NavItem[] = useMemo(() => [
    { label: t("nav.home"), href: "#home", internal: true },
    { label: t("nav.aboutTheFund"), href: "#about", internal: true },
    { label: t("nav.summaryPlan"), href: "#summary-plan", internal: true },
    { label: t("nav.planRules"), href: "#plan-rules", internal: true },
    {
      label: t("nav.resources"),
      href: `${prefix}/resources`,
      internal: true,
      children: [
        { label: t("nav.annualReports"), href: `${prefix}/resources#annual-reports` },
        { label: t("nav.newsletters"), href: `${prefix}/resources#newsletters` },
        { label: t("nav.forms"), href: `${prefix}/resources#forms` },
        { label: t("nav.whatHappensIf"), href: `${prefix}/resources#what-happens-if` },
        { label: t("nav.thingsYouNeedToKnow"), href: `${prefix}/resources#things-you-need-to-know` },
        { label: t("nav.miscellaneous"), href: `${prefix}/resources#miscellaneous` },
        { label: t("nav.links"), href: `${prefix}/resources#links` },
        { label: t("nav.rrspComparisons"), href: `${prefix}/resources#rrsp-comparisons` },
        { label: t("nav.someppNotice"), href: `${prefix}/resources#somepp-notice` },
        { label: t("nav.keyPlanDefinitions"), href: `${prefix}/resources#key-plan-definitions` },
      ],
    },
    { label: t("nav.news"), href: `${prefix}/news`, internal: true },
    { label: t("nav.faq"), href: `${prefix}/faq`, internal: true },
    { label: t("nav.member"), href: "#member", internal: true },
    { label: t("nav.contactUs"), href: `${prefix}/contact`, internal: true },
  ], [t, prefix]);

  // Watch home page sections with IntersectionObserver
  useEffect(() => {
    if (!isOnHomePage) {
      setActiveSection("");
      return;
    }

    const sectionIds = ["about", "summary-plan", "plan-rules", "member"];

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(`#${entry.target.id}`);
            return;
          }
        }
        setActiveSection("");
      },
      {
        rootMargin: "-48px 0px -70% 0px",
        threshold: 0,
      },
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [isOnHomePage]);

  const handleHashClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
      e.preventDefault();

      if (hash === "#home") {
        if (isOnHomePage) {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          navigate(prefix || "/");
        }
        return;
      }

      if (isOnHomePage) {
        const id = hash.replace("#", "");
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 56;
          window.scrollTo({ top, behavior: "smooth" });
        }
      } else {
        navigate(`${prefix || "/"}${hash}`);
      }
    },
    [isOnHomePage, navigate, prefix],
  );

  const handleMobileSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = mobileSearch.trim();
    if (trimmed) {
      navigate(`${prefix}/search?q=${encodeURIComponent(trimmed)}`);
      setMobileSearch("");
      setMobileOpen(false);
    }
  };

  const handleMobileLangSwitch = (targetLang: "en" | "fr") => {
    const isCurrentlyFrench = lang === "fr";
    const targetIsFrench = targetLang === "fr";

    // Already on the target language — just close the menu, no navigation needed
    if (isCurrentlyFrench === targetIsFrench) {
      setMobileOpen(false);
      return;
    }

    const currentPath = location.pathname;
    const currentSearch = location.search;
    const currentHash = location.hash;

    let targetUrl: string;
    if (targetLang === "en") {
      if (currentPath === "/fr") {
        targetUrl = "/" + currentSearch + currentHash;
      } else {
        targetUrl = currentPath.replace(/^\/fr/, "") + currentSearch + currentHash;
      }
    } else {
      if (currentPath === "/") {
        targetUrl = "/fr" + currentSearch + currentHash;
      } else {
        targetUrl = "/fr" + currentPath + currentSearch + currentHash;
      }
    }

    setMobileOpen(false);
    navigate(targetUrl);
  };

  const linkClass = (isChild: boolean) =>
    `flex items-center gap-1 ${
      isChild
        ? "px-4 py-2.5 text-sm text-gray-600 hover:text-[#0d1f4c] hover:bg-[#f0f6fb]"
        : "px-4 py-4 text-sm font-medium whitespace-nowrap"
    } transition-colors cursor-pointer`;

  const renderLink = (item: NavItem | NavChild, isChild = false) => {
    const href = item.href;
    const label = item.label;

    if (href.startsWith("#")) {
      return (
        <a
          href={`${prefix}${href}`}
          onClick={(e) => handleHashClick(e, href)}
          className={linkClass(isChild)}
        >
          {label}
          {"children" in item && item.children && !isChild && (
            <i className="ri-arrow-down-s-line text-xs opacity-60"></i>
          )}
        </a>
      );
    }

    if (href.startsWith("/")) {
      return (
        <Link to={href} className={linkClass(isChild)}>
          {label}
          {"children" in item && item.children && !isChild && (
            <i className="ri-arrow-down-s-line text-xs opacity-60"></i>
          )}
        </Link>
      );
    }

    return (
      <a
        href={href}
        target="_blank"
        rel="nofollow"
        className={linkClass(isChild)}
      >
        {label}
        {"children" in item && item.children && !isChild && (
          <i className="ri-arrow-down-s-line text-xs opacity-60"></i>
        )}
      </a>
    );
  };

  const isActive = (item: NavItem) => {
    if (isOnHomePage) {
      if (item.label === t("nav.home") && activeSection === "") return true;
      if (item.href.startsWith("#") && item.href === activeSection) return true;
    } else {
      if (item.href.startsWith("#") && location.hash === item.href) return true;
    }
    if (item.href.startsWith(`${prefix}/resources`) && location.pathname.startsWith(`${prefix}/resources`)) return true;
    if (item.href.startsWith(`${prefix}/faq`) && location.pathname.startsWith(`${prefix}/faq`)) return true;
    if (item.href.startsWith(`${prefix}/news`) && location.pathname.startsWith(`${prefix}/news`)) return true;
    if (item.href.startsWith(`${prefix}/contact`) && location.pathname === `${prefix}/contact`) return true;
    return false;
  };

  return (
    <nav className="bg-white border-b border-gray-200 w-full sticky top-0 z-50 shadow-sm">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Desktop nav */}
        <ul className="hidden md:flex items-stretch">
          {navItems.map((item) => (
            <li
              key={item.label}
              className="relative"
              onMouseEnter={() => item.children && setOpenDropdown(item.label)}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <div
                className={`border-b-2 transition-all duration-400 ease-out ${
                  isActive(item)
                    ? "text-[#0d1f4c] border-[#1a8fc1]"
                    : "text-gray-600 border-transparent hover:text-[#0d1f4c] hover:border-[#1a8fc1]/50"
                }`}
              >
                {renderLink(item)}
              </div>

              {item.children && openDropdown === item.label && (
                <ul className="absolute top-full left-0 bg-white border border-gray-100 rounded-b-lg shadow-lg min-w-[240px] z-50 py-1">
                  {item.children.map((child) => (
                    <li key={child.label}>
                      {renderLink(child, true)}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>

        {/* Mobile header */}
        <div className="md:hidden flex items-center justify-between py-3">
          <span className="text-[#0d1f4c] font-semibold text-sm">{t("nav.mobileTitle")}</span>
          <button
            className="text-[#0d1f4c] cursor-pointer w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={t("nav.toggleAria")}
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl`}></i>
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-gray-100">
            {/* A. Navigation links */}
            <ul>
              {navItems.map((item) => (
                <li key={item.label}>
                  <div className="flex items-center justify-between">
                    {item.href.startsWith("#") ? (
                      <a
                        href={`${prefix}${item.href}`}
                        onClick={(e) => {
                          handleHashClick(e, item.href);
                          setMobileOpen(false);
                        }}
                        className="flex-1 block px-2 py-2.5 text-sm text-gray-700 hover:text-[#0d1f4c] hover:bg-[#f0f6fb] transition-colors cursor-pointer rounded"
                      >
                        {item.label}
                      </a>
                    ) : item.href.startsWith("/") ? (
                      <Link
                        to={item.href}
                        className="flex-1 block px-2 py-2.5 text-sm text-gray-700 hover:text-[#0d1f4c] hover:bg-[#f0f6fb] transition-colors cursor-pointer rounded"
                        onClick={() => setMobileOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="nofollow"
                        className="flex-1 block px-2 py-2.5 text-sm text-gray-700 hover:text-[#0d1f4c] hover:bg-[#f0f6fb] transition-colors cursor-pointer rounded"
                      >
                        {item.label}
                      </a>
                    )}
                    {item.children && (
                      <button
                        className="px-2 py-2.5 text-gray-400 cursor-pointer"
                        onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                      >
                        {mobileExpanded === item.label
                          ? <i className="ri-arrow-up-s-line text-sm"></i>
                          : <i className="ri-arrow-down-s-line text-sm"></i>
                        }
                      </button>
                    )}
                  </div>
                  {item.children && mobileExpanded === item.label && (
                    <ul className="pl-4 border-l-2 border-[#1a8fc1]/30 ml-2 mb-1">
                      {item.children.map((child) => (
                        <li key={child.label}>
                          <Link
                            to={child.href}
                            className="block px-3 py-2 text-xs text-gray-500 hover:text-[#0d1f4c] hover:bg-[#f0f6fb] transition-colors cursor-pointer rounded"
                            onClick={() => setMobileOpen(false)}
                          >
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>

            {/* B. Search */}
            <div className="px-2 pt-3">
              <form onSubmit={handleMobileSearch} className="flex items-stretch">
                <input
                  type="search"
                  placeholder={isFrench ? "Recherche..." : "Search..."}
                  value={mobileSearch}
                  onChange={(e) => setMobileSearch(e.target.value)}
                  className="flex-1 text-sm px-3 py-2.5 rounded-l-md border border-gray-300 bg-white text-gray-700 placeholder-gray-400 outline-none focus:border-[#1a8fc1] focus:ring-1 focus:ring-[#1a8fc1]/30 transition-all"
                  aria-label={isFrench ? "Recherche" : "Search"}
                />
                <button
                  type="submit"
                  className="px-4 py-2.5 bg-[#1a8fc1] hover:bg-[#1578a3] text-white rounded-r-md transition-colors cursor-pointer whitespace-nowrap"
                  aria-label={isFrench ? "Lancer la recherche" : "Submit search"}
                >
                  <i className="ri-search-line text-sm"></i>
                </button>
              </form>
            </div>

            {/* C. Portal actions */}
            <div className="flex flex-col gap-2 px-2 pt-3">
              <a
                href="https://www.mpfcanada.ca/trustees/"
                target="_blank"
                rel="nofollow"
                className="flex items-center justify-center gap-2 px-4 py-3 border border-[#0d1f4c]/20 text-[#0d1f4c] text-sm font-medium rounded-md hover:bg-[#0d1f4c]/5 transition-colors cursor-pointer"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-team-line text-sm"></i>
                {t("header.trustees")}
              </a>
              <a
                href={getMemberPortalUrl(isFrench ? 'fr' : 'en')}
                target="_blank"
                rel="nofollow"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-[#0d1f4c] hover:bg-[#0a1536] text-white text-sm font-semibold rounded-md transition-colors cursor-pointer"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-user-line text-sm"></i>
                {t("header.memberLogin")}
              </a>
            </div>

            {/* D. Language switch */}
            <div className="flex items-center justify-center gap-2 px-2 pt-3 text-sm font-medium">
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMobileLangSwitch("en"); }}
                className={`py-2.5 px-4 rounded-md transition-colors cursor-pointer ${!isFrench ? "bg-[#0d1f4c] text-white" : "text-gray-600 hover:text-[#0d1f4c] hover:bg-gray-100"}`}
              >
                {t("header.english")}
              </a>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMobileLangSwitch("fr"); }}
                className={`py-2.5 px-4 rounded-md transition-colors cursor-pointer ${isFrench ? "bg-[#0d1f4c] text-white" : "text-gray-600 hover:text-[#0d1f4c] hover:bg-gray-100"}`}
              >
                {t("header.francais")}
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}